/**
 * this is just a wrapper around react-toastify
 *
 * we need this as, at some point of time the toast library might change
 * and we don't want to change all the places where we use it
 *
 * so we reuse this file to import the library
 * and export it again
 *
 * USAGE - https://wollmilchsau.atlassian.net/wiki/x/A4DrIgE
 *
 * TODO:
 * - to be able to render components inside the toast
 */
import { IconCheck } from "@/assets/icons/IconCheck_small";
import { IconClose } from "@/assets/icons/IconClose_small";
import { IconWarning } from "@/assets/icons/IconWarning_small";
import { IconLoading } from "@/assets/icons/IconLoading_small";
import { RouteMap } from "@/config/RouteMap";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { ToastContainer, toast } from "react-toastify";
import "@/styles/toast.scss";

/**
 * JSToastComponent displays a toast notification with customizable content and style based on the type.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.closeToast - Callback to close the toast notification.
 * @param {Object} props.toastProps - Additional properties for the toast, such as autoClose duration.
 * @param {number} [props.toastProps.autoClose] - Duration in milliseconds before the toast auto-closes.
 * @param {Object} props.data - Data for the toast content.
 * @param {string} [props.data.text] - The main message text of the toast.
 * @param {string} [props.data.type] - Type of toast ("success", "notice", "warning", "loading").
 * @param {string} [props.data.title] - Optional title for the toast.
 * @returns {JSX.Element} The rendered toast notification component.
 */
const JSToastComponent = ({ closeToast, toastProps, data }) => {
  const { text, type = "success", title: specifiedTitle } = data || {};
  const { t } = useTranslation();
  const title = specifiedTitle || t(`v2_toast_${type}`);
  const autoClose = toastProps?.autoClose;

  const isSuccess = type === "success";
  const isNotice = type === "notice";
  const isWarning = type === "warning";
  const isLoading = type === "loading";

  const containerClass = isLoading
    ? "toast-loading"
    : isNotice
      ? "toast-notice"
      : isWarning
        ? "toast-warning"
        : "toast-success";

  const bgColor = isLoading
    ? "var(--primary-10)"
    : isNotice
      ? "var(--yellow-10)"
      : isWarning
        ? "var(--red-10)"
        : "var(--green-10)";

  return (
    <div
      className={`js-toast-item-container ${containerClass}`}
      style={{
        "--toast-duration": `${autoClose}ms`,
        "--toast-bg-color": bgColor,
      }}
    >
      <div className="js-toast-heading">
        {isSuccess && <IconCheck fill="var(--green-100)" />}
        {isLoading && <IconLoading fill="var(--primary-50)" className="icon-loading-spin" />}
        {isNotice && <IconWarning fill="var(--yellow-100)" />}
        {isWarning && <IconWarning fill="var(--red-100)" />}
        {title && <p className="js-toast-title">{title}</p>}
        <IconClose fill="var(--terciary-30)" onClick={closeToast} />
      </div>
      <p className="js-toast-body">{text}</p>
    </div>
  );
};

/**
 * Displays a toast notification with customizable options and positioning logic.
 *
 * REFER - USAGE - https://wollmilchsau.atlassian.net/wiki/x/A4DrIgE
 *
 * @param {string} text - The message text to display in the toast.
 * @param {string} type - The type of toast (e.g., "warning", "notice", etc.).
 * @param {Object} [options={}] - Additional options for the toast.
 * @param {boolean} [options.noAutoPositioning] - If true, disables automatic positioning based on modal state.
 * @param {string} [options.className] - Additional CSS class names for the toast.
 * @param {string} [options.position] - Position of the toast (e.g., "top-right", "top-left").
 * @param {string} [options.title] - Optional title for the toast.
 * @param {number} [options.autoClose] - Duration in milliseconds before the toast auto-closes.
 * @param {boolean} [options.hideProgressBar] - If true, hides the progress bar.
 * @param {boolean} [options.closeButton] - If true, shows a close button.
 * @param {boolean} [options.pauseOnHover] - If true, pauses auto-close on hover.
 * @param {boolean} [options.pauseOnFocusLoss] - If true, pauses auto-close when focus is lost.
 * @returns {React.ReactText} The toast identifier returned by the toast library.
 */
const displayToast = (text, type, options = {}) => {
  // we check for modal.show but exclude modal-sm
  const isAnyModalOpen = document.querySelector(".modal.show:not(.modal-sm)") !== null;
  const noAutoPositioning = options.noAutoPositioning ?? false;
  const defaultOptions = {
    autoClose: type === "warning" || type === "notice" || type === "error" ? 8000 : 5000,
    hideProgressBar: true,
    closeButton: false,
    pauseOnHover: true,
    pauseOnFocusLoss: false,
    ...options,
    className: `${options.className || ""} js-toast-item`.trim(),
    position: isAnyModalOpen && !noAutoPositioning ? "top-left" : options.position || "top-right",
  };

  return toast((props) => <JSToastComponent {...props} />, {
    ...defaultOptions,
    data: { text, type, title: options?.title ?? "" },
    containerId: "js-default",
  });
};

displayToast.success = (text, options = {}) => {
  return displayToast(text, "success", { ...options });
};

displayToast.notice = (text, options = {}) => {
  return displayToast(text, "notice", { ...options });
};

displayToast.warning = (text, options = {}) => {
  return displayToast(text, "warning", { ...options });
};


displayToast.loading = (text, options = {}) => {
  return displayToast(text, "loading", {
    ...options,
    autoClose: false, // Loading toasts don't auto-close
  });
};

displayToast.dismiss = (toastId) => {
  if (toastId === undefined) {
    return toast.dismiss({ containerId: "js-default" });
  }
  return toast.dismiss(toastId);
};

/**
 * Updates an existing toast with new content and/or type.
 * Useful for changing a loading toast to success/error, or updating progress messages.
 *
 * USAGE EXAMPLES:
 *
 * const toastId = displayToast.loading("Uploading...");
 *
 * displayToast.update(toastId, {
 *   text: "Upload complete!",
 *   type: "success"
 * });
 *
 * @param {React.ReactText} toastId - The ID of the toast to update (returned from displayToast methods).
 * @param {Object} config - Configuration object for the update.
 * @param {string} config.text - The new message text.
 * @param {string} [config.type="success"] - The new type ("success", "notice", "warning", "loading").
 * @param {string} [config.title] - Optional new title.
 * @param {number|false} [config.autoClose] - Duration before auto-close, or false to disable.
 * @param {Object} [config.options={}] - Additional toast options.
 * @returns {void}
 */
displayToast.update = (toastId, config = {}) => {
  const { text, type = "success", title, autoClose, ...restOptions } = config;

  const isAnyModalOpen = document.querySelector(".modal.show:not(.modal-sm)") !== null;
  const noAutoPositioning = restOptions.noAutoPositioning ?? false;

  toast.update(toastId, {
    render: (props) => <JSToastComponent {...props} />,
    data: {
      text,
      type,
      title: title ?? "",
    },
    type: type,
    autoClose: autoClose !== undefined ? autoClose : type === "warning" || type === "notice" || type === "error" ? 8000 : 5000,
    className: `${restOptions.className || ""} js-toast-item`.trim(),
    position: isAnyModalOpen && !noAutoPositioning ? "top-left" : restOptions.position || "top-right",
    hideProgressBar: true,
    closeButton: false,
    pauseOnHover: true,
    pauseOnFocusLoss: false,
    containerId: "js-default",
    icon: false,
    ...restOptions,
  });
};

/**
 * Displays a promise-based toast that shows pending, success, and error states automatically.
 *
 * USAGE EXAMPLES: - https://wollmilchsau.atlassian.net/wiki/x/A4DrIgE
 *
 * displayToast.promise(
 *   fetchData(),
 *   {
 *     pending: "Loading data...",
 *     success: "Data loaded successfully!",
 *     error: "Failed to load data"
 *   }
 * );
 *
 * displayToast.promise(
 *   saveUser(userData),
 *   {
 *     pending: { text: "Saving user...", title: "Please wait" },
 *     success: { text: "User saved!", title: "Success" },
 *     error: { text: "Failed to save user", title: "Error" }
 *   },
 *   { position: "top-left" }
 * );
 *
 *
 * @param {Promise} promise - The promise to track.
 * @param {Object} messages - Messages for each state (pending, success, error).
 * @param {string|Object} messages.pending - Message or config for pending state.
 * @param {string|Object} messages.success - Message or config for success state.
 * @param {string|Object|Function} messages.error - Message, config, or render function for error state.
 * @param {Object} [options={}] - Additional options for the toast (position, autoClose, etc.).
 * @returns {Promise} The original promise (for chaining).
 */
displayToast.promise = (promise, messages, options = {}) => {
  const isAnyModalOpen = document.querySelector(".modal.show:not(.modal-sm)") !== null;
  const noAutoPositioning = options.noAutoPositioning ?? false;
  const defaultPosition = isAnyModalOpen && !noAutoPositioning ? "top-left" : options.position || "top-right";

  const formatMessage = (message, type) => {
    if (typeof message === "string") {
      return {
        render: (props) => <JSToastComponent {...props} />,
        data: { text: message, type, title: "" },
      };
    } else if (typeof message === "object" && message !== null) {
      if (typeof message.render === "function") {
        return {
          render: message.render,
        };
      }
      return {
        render: (props) => <JSToastComponent {...props} />,
        data: {
          text: message.text || "",
          type,
          title: message.title || "",
        },
      };
    }
    return message;
  };

  const commonOptions = {
    className: `${options.className || ""} js-toast-item`.trim(),
    position: defaultPosition,
    hideProgressBar: true,
    closeButton: false,
    pauseOnHover: true,
    pauseOnFocusLoss: false,
    icon: false,
  };

  const promiseOptions = {
    pending: {
      ...formatMessage(messages.pending, "loading"),
      ...commonOptions,
    },
    success: {
      ...formatMessage(messages.success, "success"),
      ...commonOptions,
      autoClose: options.autoClose || 5000,
    },
    error: {
      ...formatMessage(messages.error, "warning"),
      ...commonOptions,
      autoClose: options.autoClose || 8000,
    },
  };

  return toast.promise(promise, promiseOptions, {
    containerId: "js-default",
    icon: false,
    ...options,
  });
};

const JSToastContainer = (props) => {
  const noTopMarginRoutes = [RouteMap.assets];
  const location = useLocation();

  const noTopMargin = noTopMarginRoutes.some((route) => location.pathname.includes(route));

  useEffect(() => {
    // clear all toasts
    console.log("Location changed, dismissing toasts", location.pathname);
    toast.dismiss({ containerId: "js-default" });
  }, [location.pathname]);

  return (
    <ToastContainer
      className={`js-toast-container ${noTopMargin ? "" : "top-margin"}`}
      {...props}
      containerId="js-default"
    />
  );
};

export { JSToastContainer };
export default displayToast;
